import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { pullUiPrefs } from "../lib/uiPrefs";
import { SESSION_EXPIRED_EVENT } from "../lib/authInterceptor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("snagger_token"));
  const [loading, setLoading] = useState(true);

  // c61_116 — every API router now requires auth (security audit);
  // attach the token globally so callers without explicit headers work.
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  useEffect(() => {
    if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    else delete axios.defaults.headers.common["Authorization"];
  }, [token]);

  const logout = useCallback(() => {
    localStorage.removeItem("snagger_token");
    sessionStorage.removeItem("ss_landed");   // c61_797 — the next sign-in lands on the account's start page again
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  }, []);

  // c61_784 — any API call answered "Token expired / Session expired (new deployment)"
  // signs the tab out right away (once) and says why, instead of every button failing
  // with that text. The login screen renders in place, so the same page comes back.
  const expiredOnce = useRef(false);
  useEffect(() => {
    const onDead = (ev) => {
      if (expiredOnce.current || !localStorage.getItem("snagger_token")) return;
      expiredOnce.current = true;
      const why = String(ev?.detail || "");
      const msg = /deployment/i.test(why)
        ? "Snagger was just updated — please sign in again to continue."
        : "Your session expired — please sign in again to continue.";
      logout();
      // the click that hit the dead session also toasts its own "Token expired" a tick later — replace it
      setTimeout(() => { toast.dismiss(); toast.error(msg, { duration: 10000, id: "session-expired" }); }, 150);
      setTimeout(() => { expiredOnce.current = false; }, 5000);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onDead);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onDead);
  }, [logout]);

  // Refresh user data from /me to get fresh permissions
  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.success) setUser(res.data.user);
    } catch {}
  }, [token]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (res.data?.success) setUser(res.data.user); else logout(); })
      .catch(() => logout())
      .finally(() => setLoading(false));
  }, [token, logout]);

  const applyLogin = useCallback((data) => {
    localStorage.setItem("snagger_token", data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    setToken(data.token);
    setUser(data.user);
  }, []);

  const login = async (username, password, code, opts = {}) => {
    const body = { username, password };
    if (opts.sms) body.sms_code = code || undefined;
    else body.mfa_code = code || undefined;
    const res = await axios.post(`${API}/login`, body);
    if (res.data?.success) applyLogin(res.data);
    return res.data;
  };

  const sendSmsCode = async (username, password, channel = "sms") => {
    const res = await axios.post(`${API}/login/sms/send`, { username, password, channel });
    return res.data;
  };

  const register = async (username, password) => {
    const res = await axios.post(`${API}/register`, { username, password });
    if (res.data?.success) {
      localStorage.setItem("snagger_token", res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
    }
    return res.data;
  };

  // c61_663 — pull the server copy of the UI prefs (drawer pin / rail / hub order) once per sign-in
  useEffect(() => {
    if (!(user?.username && token)) return;
    // c61_867 — after the server prefs land, drop restored tabs for hubs this account cannot open
    pullUiPrefs(user.username).then(() => import("../lib/workspaceTabs").then((m) => m.pruneTabsForUser(user))).catch(() => {});
  }, [user?.username, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasPermission = useCallback((tabId) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    const perms = user.permissions || [];
    // Backward-compat: when we split the old `channel_hub` permission into
    // `connection_hub` (real Connection Hub page) and a new `channel_hub`
    // (Webgility-style placeholder), existing users with `channel_hub` in
    // their permissions list still need access to Connection Hub until an
    // admin updates their row.
    if (tabId === "connection_hub" && perms.includes("channel_hub")) return true;
    return perms.includes(tabId);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, sendSmsCode, register, logout, hasPermission, refreshUser, applyLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
